import React, { useState, useReducer, useEffect, useMemo } from "react";
import { useService } from "./ServiceContext";
import { useSessionStorage } from "../utils/useSessionStorage";
import { v4 as uuidv4 } from "uuid"; // For generating unique userSession UUID
import { isEmpty } from "lodash";
import { useIntl } from "react-intl";
import { fireAlert } from "../utils/alerts";
import ErrorSummary from "../components/ProductCreation/ErrorSummary";
import { format } from "date-fns";

const initialState = {
  seriesId: "",
  seriesName: "",
  version: "",
  classEtim: "",
  comparison1: "",
  comparison2: "",
  comparison3: "",
  groupedProducts: [],
  existingCommercialClasses: [],
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_SERIES_ID":
      return { ...state, seriesId: action.payload };
    case "SET_SERIES_NAME":
      return { ...state, seriesName: action.payload };
    case "SET_VERSION":
      return { ...state, version: action.payload };
    case "SET_CLASS_ETIM":
      return { ...state, classEtim: action.payload };
    case "ADD_GROUPED_PRODUCT":
      if (Array.isArray(action.payload)) {
        const newGroups = action.payload.filter((newGroup) => !state.groupedProducts.some((existingGroup) => existingGroup.commercialClassETIM === newGroup.commercialClassETIM));
        newGroups.forEach((g) => {
          if (isEmpty(g.id)) g.id = uuidv4();
        });
        return { ...state, groupedProducts: [...state.groupedProducts, ...newGroups] };
      } else {
        if (!state.groupedProducts.some((existingGroup) => existingGroup.commercialClassETIM === action.payload.commercialClassETIM)) {
          const newGroup = [{ ...action.payload, id: action.payload.id ?? uuidv4() }];
          return { ...state, groupedProducts: [...state.groupedProducts, ...newGroup] };
        }
      }
      return state;
    case "UPDATE_GROUPED_PRODUCTS":
      if (state.groupedProducts.some((p) => p.id === action.payload.id)) {
        return {
          ...state,
          groupedProducts: state.groupedProducts.map((p) =>
            p.id === action.payload.id
              ? { ...p, commercialClassETIM: action.payload.commercialClassETIM, products: action.payload.products, parameterValues: action.payload.parameterValues }
              : p
          ),
        };
      }
      return state;
    case "REMOVE_GROUPED_PRODUCT":
      return {
        ...state,
        groupedProducts: state.groupedProducts.filter((p) => p.commercialClassETIM !== action.payload.commercialClassETIM),
      };
    case "CLEAR_GROUPED_PRODUCTS":
      return { ...state, groupedProducts: [] };
    case "SET_COMPARISON1":
      return { ...state, comparison1: action.payload };
    case "SET_COMPARISON2":
      return { ...state, comparison2: action.payload };
    case "SET_COMPARISON3":
      return { ...state, comparison3: action.payload }; // Fixed to update comparison3
    case "RESET":
      return initialState;
    case "SET_EXISTING_ETIM_COMMERICAL_CLASS":
      return {
        ...state,
        existingCommercialClasses: action.payload,
      };
    default:
      return state;
  }
};

const sessionState = {
  etimClasses: [],
};

const ETIMContext = React.createContext();

function EtimProvider({ id, classEtim, ...props }) {
  const [state, setState, invalidateState] = useSessionStorage("ETIM.EtimClasses", sessionState);
  const [etim, setEtim] = useReducer(reducer, { ...initialState, seriesId: id, classEtim: classEtim, comparison1: classEtim });
  const [etimClasses, setEtimClasses] = useState([]);
  const [products, setProducts] = useState([]);
  const [versions, setVersions] = useState([]);
  const [features, setFeatures] = useState({
    comp1: [],
    comp2: [],
    comp3: [],
  });
  const intl = useIntl();
  const [isDirty, setIsDirty] = useState(false);

  const { etimClassCommercialExistenceProvider } = useService();

  const { etimSeriesProvider, etimListProvider, etimVersionsProvider, etimProductsProvider } = useService();

  useEffect(() => {
    const initialize = async () => {
      const versionsData = await etimVersionsProvider.get();
      setVersions(versionsData);

      if (!etim.seriesId) return;

      const series = await etimSeriesProvider.get({ seriesId: etim.seriesId, classEtim: etim.classEtim });
      const productsData = await etimProductsProvider.get({ seriesId: etim.seriesId });
      const defaultVersion = isEmpty(etim.version) ? versionsData.find((v) => v.default === 1)?.version || versionsData[0]?.version : etim.version;

      setEtim({ type: "SET_VERSION", payload: defaultVersion });

      setProducts(productsData);
      setEtim({ type: "SET_SERIES_NAME", payload: series.name });

      let classes = state.etimClasses.find((e) => e.version === defaultVersion)?.classes ?? null;

      if (!classes) {
        const response = await etimListProvider.get(defaultVersion);
        classes = response?.map((c) => ({ value: c.id, label: c.class })) ?? null;
        if (classes) {
          setState({ ...state, etimClasses: [...state.etimClasses, { version: defaultVersion, classes }] });
        }
        setState({ ...state, etimClasses: [...state.etimClasses, { version: defaultVersion, classes: classes }] });
      }

      setEtimClasses(classes);
    };

    initialize();
  }, [etim.seriesId, etim.classEtim, etim.version, etimSeriesProvider, etimListProvider, etimVersionsProvider]);

  const setErrorType = (messageId, defaultMessage) => {
    return {
      errorFormattedMessage: {
        id: messageId,
        defaultMessage: defaultMessage || messageId,
      },
    };
  };

  const checkETIMValidity = async () => {
    let errors = {};

    const checkedCommercialClasses = [...etim.existingCommercialClasses.map((c) => c?.toLowerCase())];

    for (const group of etim.groupedProducts) {
      const commercialClassETIM = group.commercialClassETIM.trim().toLowerCase();

      if (isEmpty(commercialClassETIM)) {
        errors.etimClassCommercial = {
          ...setErrorType("error.required_attribute", "ETIM Commerical Class is required"),
          formattedMessage: { id: "attribute.CommercialClassETIM", defaultMessage: "Commercial Class ETIM" },
        };
        continue;
      }

      if (!group.new && checkedCommercialClasses.includes(commercialClassETIM)) continue;

      let result = {};
      result.exists = checkedCommercialClasses.includes(commercialClassETIM);

      if (!result.exists) result = await etimClassCommercialExistenceProvider.get(group.commercialClassETIM);

      if (result.exists) {
        errors.etimClassCommercial = {
          ...setErrorType("error.already_taken_attribute", "Already taken"),
          formattedMessage: {
            id: "attribute.CommercialClassETIM",
            defaultMessage: "COmmercial Class ETIM",
          },
        };
      } else {
        checkedCommercialClasses.push(commercialClassETIM);
      }
    }

    return errors;
  };

  async function validate() {
    let errors = await checkETIMValidity();
    if (Object.keys(errors).length === 0) {
      return true;
    } else {
      fireAlert(<ErrorSummary intl={intl} groupErrors={errors}></ErrorSummary>, "Incorrect attributes", "error");
      return false;
    }
  }

  const contextValue = { isDirty, setIsDirty, setFeatures, setEtim, validate };
  const memoContextValue = useMemo(
    () => ({
      etim,
      etimClasses,
      versions,
      features,
      products,
    }),
    [etim, etimClasses, versions, features, products, checkETIMValidity]
  );

  return <ETIMContext.Provider value={{ ...contextValue, ...memoContextValue }} {...props} />;
}

function useEtim() {
  const context = React.useContext(ETIMContext);
  if (context === undefined) {
    throw new Error("useEtime must be used within an EtimProvider");
  }
  return context;
}

export { EtimProvider, useEtim };
